import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Zap, Calendar, Users, User, Ticket, Crown, Shield, LogOut, Building2, CalendarDays, CalendarHeart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/services/api';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, loading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  // Role lives in the DB (the /auth/me payload only exposes user_type), so resolve
  // it from /users/me to decide whether to expose admin links. Routes stay
  // accessible regardless; this only controls link visibility.
  useEffect(() => {
    let active = true;
    if (isAuthenticated && api.isAuthenticated()) {
      api.get('/users/me')
        .then((me) => { if (active) setIsAdmin(me?.role === 'admin'); })
        .catch(() => { if (active) setIsAdmin(false); });
    } else {
      setIsAdmin(false);
    }
    return () => { active = false; };
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Don't show user-specific UI while loading
  const showAuthenticatedUI = isAuthenticated && user && !loading;

  const navItems = [
    { path: '/', label: 'Discover', icon: Zap },
    { path: '/events', label: 'Events', icon: Calendar },
    { path: '/djs', label: 'DJs', icon: Users },
    // Tickets and My Events are personal — only shown to authenticated users.
    ...(showAuthenticatedUI ? [{ path: '/tickets', label: 'Tickets', icon: Ticket }] : []),
    ...(showAuthenticatedUI ? [{ path: '/my-events', label: 'My Events', icon: CalendarHeart }] : []),
    { path: '/membership', label: 'Wolves Pass', icon: Crown }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-border/50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img
              src="/logo.png"
              alt="SoundWolves Logo"
              className="h-8 object-contain"
            />
            <span className="font-display font-bold text-xl tracking-tight hidden sm:block">
              <span className="text-foreground">SOUND</span>
              <span className="text-gradient-gold">WOLVES</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant={isActive ? 'default' : 'ghost'}
                    size="sm"
                    className={cn(
                      'flex items-center gap-2',
                      isActive && 'shadow-glow',
                      item.highlight && !isActive && 'text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10'
                    )}
                  >
                    <Icon className={cn('w-4 h-4', item.highlight && !isActive && 'text-cyan-400')} />
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </div>

          {/* User Actions */}
          <div className="hidden md:flex items-center space-x-3">
            {showAuthenticatedUI ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {user.dj_name || user.name}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    {user.user_type === 'dj' && (
                      <DropdownMenuItem onClick={() => navigate('/dj-dashboard')} className="cursor-pointer">
                        <User className="w-4 h-4 mr-2" /> DJ Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/dj-status')} className="cursor-pointer">
                      <User className="w-4 h-4 mr-2" /> DJ Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/organizer-apply')} className="cursor-pointer">
                      <Building2 className="w-4 h-4 mr-2" /> Apply as Organizer
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/organizer/profile')} className="cursor-pointer">
                      <Building2 className="w-4 h-4 mr-2" /> Organizer Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/organizer/events')} className="cursor-pointer">
                      <CalendarDays className="w-4 h-4 mr-2" /> Organizer Events
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                          <Shield className="w-4 h-4 mr-2" /> Admin Dashboard
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/users')} className="cursor-pointer">
                          <Shield className="w-4 h-4 mr-2" /> Admin Users
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/organizers')} className="cursor-pointer">
                          <Shield className="w-4 h-4 mr-2" /> Admin Organizers
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/djs')} className="cursor-pointer">
                          <Shield className="w-4 h-4 mr-2" /> Admin DJs
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/admin/events')} className="cursor-pointer">
                          <Shield className="w-4 h-4 mr-2" /> Admin Events
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    Login
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-foreground hover:text-primary transition-colors"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pt-4 pb-24 space-y-2 animate-fade-in max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-lg transition-all',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-glow'
                      : item.highlight
                        ? 'text-cyan-400 hover:bg-cyan-400/10'
                        : 'text-foreground hover:bg-muted'
                  )}
                >
                  <Icon className={cn('w-5 h-5', item.highlight && !isActive && 'text-cyan-400')} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            <div className="pt-4 space-y-2">
              {showAuthenticatedUI ? (
                <>
                  {user.user_type === 'dj' ? (
                    <Link to="/dj-dashboard" onClick={() => setIsOpen(false)}>
                      <Button variant="outline" className="w-full justify-start">
                        <User className="w-5 h-5 mr-2" />
                        {user.dj_name || user.name}
                      </Button>
                    </Link>
                  ) : (
                    <Button variant="outline" className="w-full justify-start" onClick={() => setIsOpen(false)}>
                      <User className="w-5 h-5 mr-2" />
                      {user.dj_name || user.name}
                    </Button>
                  )}
                  <Link to="/dj-status" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full justify-start">
                      <User className="w-5 h-5 mr-2" />
                      DJ Profile
                    </Button>
                  </Link>
                  <Link to="/organizer-apply" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full justify-start">
                      <Building2 className="w-5 h-5 mr-2" />
                      Apply as Organizer
                    </Button>
                  </Link>
                  <Link to="/organizer/profile" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full justify-start">
                      <Building2 className="w-5 h-5 mr-2" />
                      Organizer Profile
                    </Button>
                  </Link>
                  <Link to="/organizer/events" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarDays className="w-5 h-5 mr-2" />
                      Organizer Events
                    </Button>
                  </Link>
                  {isAdmin && (
                    <>
                      <Link to="/admin" onClick={() => setIsOpen(false)}>
                        <Button variant="outline" className="w-full justify-start">
                          <Shield className="w-5 h-5 mr-2" />
                          Admin Dashboard
                        </Button>
                      </Link>
                      <Link to="/admin/users" onClick={() => setIsOpen(false)}>
                        <Button variant="outline" className="w-full justify-start">
                          <Shield className="w-5 h-5 mr-2" />
                          Admin Users
                        </Button>
                      </Link>
                      <Link to="/admin/organizers" onClick={() => setIsOpen(false)}>
                        <Button variant="outline" className="w-full justify-start">
                          <Shield className="w-5 h-5 mr-2" />
                          Admin Organizers
                        </Button>
                      </Link>
                      <Link to="/admin/djs" onClick={() => setIsOpen(false)}>
                        <Button variant="outline" className="w-full justify-start">
                          <Shield className="w-5 h-5 mr-2" />
                          Admin DJs
                        </Button>
                      </Link>
                      <Link to="/admin/events" onClick={() => setIsOpen(false)}>
                        <Button variant="outline" className="w-full justify-start">
                          <Shield className="w-5 h-5 mr-2" />
                          Admin Events
                        </Button>
                      </Link>
                    </>
                  )}
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={handleLogout}
                  >
                    <LogOut className="w-5 h-5 mr-2" />
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setIsOpen(false)}>
                    <Button variant="outline" className="w-full">
                      Login / Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
